/**
 * Enhanced Organization & LocalBusiness Schema for Tigola
 * Fixes all validation errors and ensures Google Rich Results compatibility
 */

export const generateValidOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": ["Organization", "LocalBusiness", "TaxiService"],
  "@id": "https://tigola.com/#organization",

  // Basic Organization Info
  name: "Tigola",
  legalName: "Tigola UK Ltd",
  alternateName: ["Tigola UK", "Tigola Transport", "Tigola Taxis"],
  url: "https://tigola.com",

  // Required description
  description:
    "Professional airport transfer and taxi service across the UK. 24/7 availability with fixed pricing, professional drivers, and real-time tracking. Serving all major UK airports and cities.",

  // Valid logo with proper dimensions
  logo: {
    "@type": "ImageObject",
    url: "https://tigola.com/tigola-logo.webp",
    width: 512,
    height: 512,
    contentUrl: "https://tigola.com/tigola-logo.webp",
  },

  // Required business address - Leicester HQ
  address: {
    "@type": "PostalAddress",
    streetAddress: "24 Brambling Road",
    addressLocality: "Leicester",
    addressRegion: "East Midlands",
    postalCode: "LE5 3HL",
    addressCountry: "GB",
  },

  // Valid contact information
  telephone: "+44 116 298 2567",
  email: "contact-us@tigola.com",

  // Multiple contact points
  contactPoint: [
    {
      "@type": "ContactPoint",
      telephone: "+44 116 298 2567",
      contactType: "customer service",
      availableLanguage: ["en-GB", "en"],
      areaServed: "GB",
      hoursAvailable: {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
          "Sunday",
        ],
        opens: "00:00",
        closes: "23:59",
      },
    },
    {
      "@type": "ContactPoint",
      telephone: "+44 116 298 2567",
      contactType: "reservations",
      availableLanguage: ["en-GB", "en"],
      areaServed: "GB",
      hoursAvailable: {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
          "Sunday",
        ],
        opens: "00:00",
        closes: "23:59",
      },
    },
    {
      "@type": "ContactPoint",
      telephone: "+44 116 298 2567",
      contactType: "emergency",
      availableLanguage: ["en-GB", "en"],
      areaServed: "GB",
      hoursAvailable: {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
          "Sunday",
        ],
        opens: "00:00",
        closes: "23:59",
      },
    },
  ],

  // Business hours
  openingHours: ["Mo-Su 00:00-23:59"],

  // Service area
  areaServed: [
    {
      "@type": "Country",
      name: "United Kingdom",
      sameAs: "https://en.wikipedia.org/wiki/United_Kingdom",
    },
    {
      "@type": "City",
      name: "Leicester",
      addressRegion: "East Midlands",
      addressCountry: "GB",
    },
    {
      "@type": "City",
      name: "Birmingham",
      addressRegion: "West Midlands",
      addressCountry: "GB",
    },
    {
      "@type": "City",
      name: "Manchester",
      addressRegion: "Greater Manchester",
      addressCountry: "GB",
    },
    {
      "@type": "City",
      name: "London",
      addressRegion: "Greater London",
      addressCountry: "GB",
    },
  ],

  // Business details
  foundingDate: "2023",
  priceRange: "£6-£150",
  paymentAccepted: [
    "Cash",
    "Credit Card",
    "Debit Card",
    "Bank Transfer",
    "Online Payment",
  ],
  currenciesAccepted: "GBP",

  // Services offered
  serviceType: [
    "Airport Transfer",
    "Taxi Service",
    "Private Hire",
    "Executive Transport",
    "City Transport",
    "Intercity Travel",
  ],

  // Valid social media links
  sameAs: [
    "https://www.facebook.com/profile.php?id=61578755657517",
    "https://x.com/Tigola_UK",
    "https://www.linkedin.com/company/tigola-uk-ltd",
    "https://www.instagram.com/tigola_uk/",
  ],

  // Service catalog
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Transport Services",
    itemListElement: [
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          "@id": "https://tigola.com/airports",
          name: "Airport Transfer",
          description:
            "Professional airport transfer service to all major UK airports with flight tracking and meet & greet service",
        },
        priceRange: "£15-£70",
        availability: "https://schema.org/InStock",
        url: "https://tigola.com/airports",
        areaServed: "GB",
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          "@id": "https://tigola.com/taxi",
          name: "City Transport",
          description:
            "Local taxi service and city transport with professional drivers",
        },
        priceRange: "£6-£25",
        availability: "https://schema.org/InStock",
        url: "https://tigola.com/taxi",
        areaServed: "GB",
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          "@id": "https://tigola.com/business",
          name: "Executive Transport",
          description:
            "Premium executive transport for business travel and special occasions",
        },
        priceRange: "£25-£150",
        availability: "https://schema.org/InStock",
        url: "https://tigola.com/business",
        areaServed: "GB",
      },
    ],
  },

  // Business verification
  knowsAbout: [
    "Airport Transfer",
    "Taxi Service",
    "Private Hire",
    "UK Transportation",
    "Executive Transport",
    "Business Travel",
  ],

  // Additional business properties
  slogan: "Professional UK Transport Service",
  award: "5-Star Customer Service",

  // Accessibility
  hasAccessibilityFeature: [
    "Wheelchair Accessible Vehicles Available",
    "24/7 Customer Support",
    "Multiple Payment Options",
  ],
});

// Office address mapping for specific locations
const officeAddresses: Record<string, any> = {
  leicester: {
    streetAddress: "24 Brambling Road",
    addressLocality: "Leicester",
    addressRegion: "East Midlands",
    postalCode: "LE5 3HL",
  },
  birmingham: {
    streetAddress: "11 Saint Pauls Square",
    addressLocality: "Birmingham",
    addressRegion: "West Midlands",
    postalCode: "B3 1RB",
  },
  manchester: {
    streetAddress: "81 King Street",
    addressLocality: "Manchester",
    addressRegion: "Greater Manchester",
    postalCode: "M2 4AH",
  },
};

export const generateCityOrganizationSchema = (
  cityName: string,
  citySlug: string,
) => {
  const cityKey = citySlug.toLowerCase();
  const officeAddress = officeAddresses[cityKey];

  return {
    "@context": "https://schema.org",
    "@type": ["Organization", "LocalBusiness", "TaxiService"],
    "@id": `https://tigola.com/taxi/${citySlug}/#organization`,

    name: `Tigola ${cityName}`,
    legalName: "Tigola UK Ltd",
    url: `https://tigola.com/taxi/${citySlug}`,

    description: `Professional taxi and transport service in ${cityName}. 24/7 availability with fixed pricing and professional drivers. Airport transfers and local transport.`,

    logo: {
      "@type": "ImageObject",
      url: "https://tigola.com/tigola-logo.png",
      width: 512,
      height: 512,
    },

    address: {
      "@type": "PostalAddress",
      ...(officeAddress || {
        addressLocality: cityName,
        addressRegion: "UK",
      }),
      addressCountry: "GB",
    },

    telephone: "+44-20-3239-9876",
    email: "contact-us@tigola.com",

    areaServed: {
      "@type": "City",
      name: cityName,
      addressCountry: "GB",
    },

    serviceType: ["Taxi Service", "Airport Transfer", "City Transport"],
    priceRange: "£6-£70",

    openingHours: ["Mo-Su 00:00-23:59"],

    sameAs: [
      "https://www.facebook.com/profile.php?id=61578755657517",
      "https://x.com/Tigola_UK",
      "https://www.linkedin.com/company/tigola-uk-ltd",
      "https://www.instagram.com/tigola_uk/",
    ],

    parentOrganization: {
      "@id": "https://tigola.com/#organization",
    },
  };
};

export const generateAirportOrganizationSchema = (
  airportName: string,
  airportCode: string,
) => ({
  "@context": "https://schema.org",
  "@type": ["Organization", "LocalBusiness", "TaxiService"],
  "@id": `https://tigola.com/airports/${airportCode.toLowerCase()}/#organization`,

  name: `Tigola Airport Transfers - ${airportName}`,
  legalName: "Tigola UK Ltd",
  url: `https://tigola.com/airports/${airportCode.toLowerCase()}`,

  description: `Professional airport transfer service to ${airportName} (${airportCode}). Flight tracking, meet & greet service, and professional drivers.`,

  logo: {
    "@type": "ImageObject",
    url: "https://tigola.com/tigola-logo.png",
    width: 512,
    height: 512,
  },

  telephone: "+44-20-3239-9876",
  email: "contact-us@tigola.com",

  serviceType: ["Airport Transfer", "Taxi Service", "Meet & Greet Service"],
  priceRange: "£15-£70",

  openingHours: ["Mo-Su 00:00-23:59"],

  sameAs: [
    "https://www.facebook.com/profile.php?id=61578755657517",
    "https://x.com/Tigola_UK",
    "https://www.linkedin.com/company/tigola-uk-ltd",
    "https://www.instagram.com/tigola_uk/",
  ],

  parentOrganization: {
    "@id": "https://tigola.com/#organization",
  },
});
